import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Crown, 
  Bot, 
  Zap, 
  UserSearch, 
  UserX,
  X,
  Loader,
  Users,
  BookOpen,
  GraduationCap,
  ChevronDown,
  SlidersHorizontal,
  Smartphone,
  Globe
} from 'lucide-react';

const SearchAndFilters = ({
  searchQuery,
  onSearchQueryChange,
  onSearch,
  searchLoading,
  isSearchMode,
  onClearSearch,
  searchResultsCount,
  activeFilter,
  onFilterChange,
  showFilters,
  onToggleFilters,
  selectedTrainingId,
  onTrainingFilterChange,
  selectedCourseId,
  onCourseFilterChange,
  availableTrainings = [],
  availableCourses = [],
  onRefresh,
  loading,
  totalUsersCount = 0,
  premiumCount = 0,
  manualCount = 0,
  autoCount = 0,
  freeCount = 0,
  withTrainingsCount = 0,
  withCoursesCount = 0,
  telegramCount = 0,    // Добавлено
  webCount = 0          // Добавлено
}) => {
  const [showTrainingDropdown, setShowTrainingDropdown] = useState(false);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);

  const filters = [
    { id: 'all', label: 'Все пользователи', icon: Users, iconColor: 'text-blue-400', count: totalUsersCount },
    { id: 'telegram', label: 'Telegram', icon: Smartphone, iconColor: 'text-blue-400', count: telegramCount },
    { id: 'web', label: 'Web', icon: Globe, iconColor: 'text-green-400', count: webCount },
    { id: 'premium', label: 'Premium', icon: Crown, iconColor: 'text-yellow-400', count: premiumCount },
    { id: 'free', label: 'Free', icon: null, iconColor: null, count: freeCount },
    { id: 'manual', label: 'Ручная активация', icon: Bot, iconColor: 'text-purple-400', count: manualCount },
    { id: 'auto', label: 'Авто-активация', icon: Zap, iconColor: 'text-blue-400', count: autoCount },
    { id: 'withTrainings', label: 'С тренингами', icon: BookOpen, iconColor: 'text-purple-400', count: withTrainingsCount },
    { id: 'withCourses', label: 'С курсами', icon: GraduationCap, iconColor: 'text-green-400', count: withCoursesCount }
  ];

  const getFilterButtonClass = (filterId) => {
    const baseClass = "px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium flex items-center gap-2 whitespace-nowrap";
    
    if (activeFilter === filterId) {
      switch (filterId) {
        case 'premium':
          return `${baseClass} bg-yellow-900/50 text-yellow-300 border border-yellow-700 shadow-lg`;
        case 'free':
          return `${baseClass} bg-gray-600 text-gray-100 border border-gray-500 shadow-lg`;
        case 'manual':
          return `${baseClass} bg-purple-900/50 text-purple-300 border border-purple-700 shadow-lg`;
        case 'auto':
          return `${baseClass} bg-blue-900/50 text-blue-300 border border-blue-700 shadow-lg`;
        case 'withTrainings':
          return `${baseClass} bg-purple-900/50 text-purple-300 border border-purple-700 shadow-lg`;
        case 'withCourses':
          return `${baseClass} bg-green-900/50 text-green-300 border border-green-700 shadow-lg`;
        case 'telegram':
          return `${baseClass} bg-blue-900/50 text-blue-300 border border-blue-700 shadow-lg`;
        case 'web':
          return `${baseClass} bg-green-900/50 text-green-300 border border-green-700 shadow-lg`;
        default:
          return `${baseClass} bg-blue-900/50 text-blue-300 border border-blue-700 shadow-lg`;
      }
    }
    
    return `${baseClass} bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 hover:text-gray-300`;
  };

  const handleFilterClick = (filterId) => {
    if (filterId !== 'withTrainings' && filterId !== 'withCourses') {
      if (onTrainingFilterChange) onTrainingFilterChange(null);
      if (onCourseFilterChange) onCourseFilterChange(null);
    }
    onFilterChange(filterId);
  };

  const handleTrainingSelect = (trainingId) => {
    onTrainingFilterChange(trainingId);
    setShowTrainingDropdown(false);
    if (activeFilter !== 'withTrainings') {
      onFilterChange('withTrainings');
    }
  };

  const handleCourseSelect = (courseId) => {
    onCourseFilterChange(courseId);
    setShowCourseDropdown(false);
    if (activeFilter !== 'withCourses') {
      onFilterChange('withCourses');
    }
  };

  const handleClearTrainingFilter = () => {
    onTrainingFilterChange(null);
  };

  const handleClearCourseFilter = () => {
    onCourseFilterChange(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const handleClearAllFilters = () => {
    onFilterChange('all');
    onTrainingFilterChange(null);
    onCourseFilterChange(null);
  };

  const hasActiveMaterialFilter = selectedTrainingId || selectedCourseId;

  return (
    <div className="space-y-4 relative z-30">
      {/* Основная панель поиска */}
      <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Поиск по ID, username, имени, телефону или email..."
                className="w-full pl-9 sm:pl-10 pr-4 py-2.5 bg-gray-900 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm placeholder-gray-500"
              />
              {searchLoading && (
                <Loader className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 animate-spin" />
              )}
              {isSearchMode && !searchLoading && (
                <button
                  onClick={onClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onSearch}
              disabled={searchLoading}
              className="px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm shadow-lg"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Найти</span>
              <span className="sm:hidden">Поиск</span>
            </button>
            
            <button
              onClick={onRefresh}
              disabled={loading}
              className="px-4 py-2.5 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Обновить</span>
            </button>
            
            <button
              onClick={onToggleFilters}
              className="md:hidden px-4 py-2.5 bg-gray-700 text-gray-300 font-medium rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 text-sm"
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span>Фильтры</span>
            </button>
          </div>
        </div>

        {/* Десктопные фильтры */}
        <div className="hidden md:block mt-4 pt-4 border-t border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-400">Быстрые фильтры:</span>
            </div>
            {hasActiveMaterialFilter && (
              <button
                onClick={handleClearAllFilters}
                className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 bg-red-900/20 hover:bg-red-900/30 rounded-lg transition-colors"
              >
                <X className="w-3 h-3" />
                Сбросить все фильтры
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.map(filter => {
              const Icon = filter.icon;
              const isActive = activeFilter === filter.id;
              
              return (
                <button
                  key={filter.id}
                  onClick={() => handleFilterClick(filter.id)}
                  className={getFilterButtonClass(filter.id)}
                >
                  {Icon && <Icon className={`w-4 h-4 ${filter.iconColor}`} />}
                  <span>{filter.label}</span>
                  {filter.count !== undefined && (
                    <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                      isActive 
                        ? 'bg-white/20 text-white'
                        : 'bg-gray-700 text-gray-400'
                    }`}>
                      {filter.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Фильтры по конкретным тренингам */}
          {(activeFilter === 'withTrainings' || selectedTrainingId) && availableTrainings.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-gray-400 flex items-center gap-2">
                  <BookOpen className="w-3 h-3 text-purple-400" />
                  <span>Детальный фильтр по тренингу:</span>
                </div>
                {selectedTrainingId && (
                  <button
                    onClick={handleClearTrainingFilter}
                    className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Очистить
                  </button>
                )}
              </div>
              <div className="relative inline-block">
                <button
                  onClick={() => setShowTrainingDropdown(!showTrainingDropdown)}
                  className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-sm flex items-center gap-2 hover:bg-gray-600 transition-colors border border-gray-600"
                >
                  {selectedTrainingId 
                    ? availableTrainings.find(t => t.id === selectedTrainingId)?.title || 'Выбрать тренинг'
                    : 'Выбрать тренинг'}
                  <ChevronDown className={`w-3 h-3 transition-transform ${showTrainingDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showTrainingDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50 max-h-60 overflow-y-auto">
                    <div className="p-1">
                      <button
                        onClick={() => setShowTrainingDropdown(false)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-700 rounded transition-colors"
                      >
                        Отменить
                      </button>
                      <div className="border-t border-gray-700 my-1"></div>
                      {availableTrainings.map(training => (
                        <button
                          key={training.id}
                          onClick={() => handleTrainingSelect(training.id)}
                          className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                            selectedTrainingId === training.id
                              ? 'bg-purple-900/50 text-purple-300'
                              : 'text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {training.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Фильтры по конкретным курсам */}
          {(activeFilter === 'withCourses' || selectedCourseId) && availableCourses.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-gray-400 flex items-center gap-2">
                  <GraduationCap className="w-3 h-3 text-green-400" />
                  <span>Детальный фильтр по курсу:</span>
                </div>
                {selectedCourseId && (
                  <button
                    onClick={handleClearCourseFilter}
                    className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Очистить
                  </button>
                )}
              </div>
              <div className="relative inline-block">
                <button
                  onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                  className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-sm flex items-center gap-2 hover:bg-gray-600 transition-colors border border-gray-600"
                >
                  {selectedCourseId 
                    ? availableCourses.find(c => c.id === selectedCourseId)?.title || 'Выбрать курс'
                    : 'Выбрать курс'}
                  <ChevronDown className={`w-3 h-3 transition-transform ${showCourseDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showCourseDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50 max-h-60 overflow-y-auto">
                    <div className="p-1">
                      <button
                        onClick={() => setShowCourseDropdown(false)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-700 rounded transition-colors"
                      >
                        Отменить
                      </button>
                      <div className="border-t border-gray-700 my-1"></div>
                      {availableCourses.map(course => (
                        <button
                          key={course.id}
                          onClick={() => handleCourseSelect(course.id)}
                          className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                            selectedCourseId === course.id
                              ? 'bg-green-900/50 text-green-300'
                              : 'text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {course.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Мобильные фильтры */}
        {showFilters && (
          <div className="md:hidden mt-4 pt-4 border-t border-gray-700 animate-slide-down">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-400">Быстрые фильтры:</span>
              </div>
              {hasActiveMaterialFilter && (
                <button
                  onClick={handleClearAllFilters}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 bg-red-900/20 rounded-lg transition-colors"
                >
                  <X className="w-3 h-3" />
                  Сбросить
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {filters.map(filter => {
                const Icon = filter.icon;
                const isActive = activeFilter === filter.id;
                
                return (
                  <button
                    key={filter.id}
                    onClick={() => handleFilterClick(filter.id)}
                    className={`px-3 py-2 rounded-lg transition-all duration-200 text-sm font-medium flex items-center justify-center gap-2 ${
                      isActive
                        ? filter.id === 'premium'
                          ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-700'
                          : filter.id === 'free'
                          ? 'bg-gray-600 text-gray-100 border border-gray-500'
                          : filter.id === 'manual'
                          ? 'bg-purple-900/50 text-purple-300 border border-purple-700'
                          : filter.id === 'auto'
                          ? 'bg-blue-900/50 text-blue-300 border border-blue-700'
                          : filter.id === 'withTrainings'
                          ? 'bg-purple-900/50 text-purple-300 border border-purple-700'
                          : filter.id === 'withCourses'
                          ? 'bg-green-900/50 text-green-300 border border-green-700'
                          : filter.id === 'telegram'
                          ? 'bg-blue-900/50 text-blue-300 border border-blue-700'
                          : filter.id === 'web'
                          ? 'bg-green-900/50 text-green-300 border border-green-700'
                          : 'bg-blue-900/50 text-blue-300 border border-blue-700'
                        : 'bg-gray-800 text-gray-400 border border-gray-700'
                    }`}
                  >
                    {Icon && <Icon className={`w-4 h-4 ${filter.iconColor}`} />}
                    <span>{filter.label}</span>
                    {filter.count !== undefined && (
                      <span className="text-xs opacity-75">({filter.count})</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Мобильные фильтры по тренингам */}
            {(activeFilter === 'withTrainings' || selectedTrainingId) && availableTrainings.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium text-gray-400 flex items-center gap-2">
                    <BookOpen className="w-3 h-3 text-purple-400" />
                    <span>Конкретный тренинг:</span>
                  </div>
                  {selectedTrainingId && (
                    <button
                      onClick={handleClearTrainingFilter}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Очистить
                    </button>
                  )}
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowTrainingDropdown(!showTrainingDropdown)}
                    className="w-full px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm flex items-center justify-between hover:bg-gray-600 transition-colors"
                  >
                    <span className="truncate">{selectedTrainingId 
                      ? availableTrainings.find(t => t.id === selectedTrainingId)?.title || 'Выбрать тренинг'
                      : 'Выбрать тренинг'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${showTrainingDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showTrainingDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50">
                      <div className="p-1">
                        {availableTrainings.map(training => (
                          <button
                            key={training.id}
                            onClick={() => handleTrainingSelect(training.id)}
                            className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                              selectedTrainingId === training.id
                                ? 'bg-purple-900/50 text-purple-300'
                                : 'text-gray-300 hover:bg-gray-700'
                            }`}
                          >
                            <span className="truncate block">{training.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Мобильные фильтры по курсам */}
            {(activeFilter === 'withCourses' || selectedCourseId) && availableCourses.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium text-gray-400 flex items-center gap-2">
                    <GraduationCap className="w-3 h-3 text-green-400" />
                    <span>Конкретный курс:</span>
                  </div>
                  {selectedCourseId && (
                    <button
                      onClick={handleClearCourseFilter}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Очистить
                    </button>
                  )}
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                    className="w-full px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm flex items-center justify-between hover:bg-gray-600 transition-colors"
                  >
                    <span className="truncate">{selectedCourseId 
                      ? availableCourses.find(c => c.id === selectedCourseId)?.title || 'Выбрать курс'
                      : 'Выбрать курс'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${showCourseDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showCourseDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50">
                      <div className="p-1">
                        {availableCourses.map(course => (
                          <button
                            key={course.id}
                            onClick={() => handleCourseSelect(course.id)}
                            className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                              selectedCourseId === course.id
                                ? 'bg-green-900/50 text-green-300'
                                : 'text-gray-300 hover:bg-gray-700'
                            }`}
                          >
                            <span className="truncate block">{course.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Результаты поиска */}
        {isSearchMode && (
          <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <UserSearch className="w-4 h-4 text-blue-400" />
              <span className="text-gray-300">
                Результаты поиска: <span className="font-semibold text-blue-400">{searchResultsCount}</span> пользователей
              </span>
            </div>
            <button 
              onClick={onClearSearch} 
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-gray-300 bg-gray-700 rounded-lg transition-colors hover:bg-gray-600 flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              Очистить
            </button>
          </div>
        )}

        {/* Индикатор активного фильтра по материалу - улучшенный дизайн */}
        {hasActiveMaterialFilter && (
          <div className="mt-3 p-2 bg-gray-700/30 rounded-lg border border-gray-600">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-400">Активные фильтры:</span>
                {selectedTrainingId && (
                  <span className="px-2 py-0.5 bg-purple-900/50 text-purple-300 rounded-full flex items-center gap-1 text-xs">
                    <BookOpen className="w-3 h-3" />
                    <span className="max-w-[200px] truncate">
                      {availableTrainings.find(t => t.id === selectedTrainingId)?.title}
                    </span>
                    <button onClick={handleClearTrainingFilter} className="hover:text-purple-100 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedCourseId && (
                  <span className="px-2 py-0.5 bg-green-900/50 text-green-300 rounded-full flex items-center gap-1 text-xs">
                    <GraduationCap className="w-3 h-3" />
                    <span className="max-w-[200px] truncate">
                      {availableCourses.find(c => c.id === selectedCourseId)?.title}
                    </span>
                    <button onClick={handleClearCourseFilter} className="hover:text-green-100 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
              <button
                onClick={handleClearAllFilters}
                className="text-xs text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Сбросить все
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Мобильная статистика - улучшенный вид */}
      <div className="md:hidden bg-gray-800 rounded-xl p-4 border border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-400">Статистика</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center justify-between bg-gray-700/50 px-3 py-2 rounded-lg">
            <span className="text-xs text-gray-400">Всего</span>
            <span className="font-semibold text-white">{totalUsersCount}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-700/50 px-3 py-2 rounded-lg">
            <span className="text-xs text-gray-400">Premium</span>
            <span className="font-semibold text-yellow-400">{premiumCount}</span>
          </div>
          <div className="flex items-center justify-between bg-blue-900/30 px-3 py-2 rounded-lg">
            <span className="text-xs text-gray-400">Telegram</span>
            <span className="font-semibold text-blue-400">{telegramCount}</span>
          </div>
          <div className="flex items-center justify-between bg-green-900/30 px-3 py-2 rounded-lg">
            <span className="text-xs text-gray-400">Web</span>
            <span className="font-semibold text-green-400">{webCount}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-700/50 px-3 py-2 rounded-lg">
            <span className="text-xs text-gray-400">Авто</span>
            <span className="font-semibold text-blue-400">{autoCount}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-700/50 px-3 py-2 rounded-lg">
            <span className="text-xs text-gray-400">Ручные</span>
            <span className="font-semibold text-purple-400">{manualCount}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-700/50 px-3 py-2 rounded-lg">
            <span className="text-xs text-gray-400">С тренингами</span>
            <span className="font-semibold text-purple-400">{withTrainingsCount}</span>
          </div>
          <div className="flex items-center justify-between bg-gray-700/50 px-3 py-2 rounded-lg">
            <span className="text-xs text-gray-400">С курсами</span>
            <span className="font-semibold text-green-400">{withCoursesCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchAndFilters;